<template>
  <div class="flex flex-col min-h-screen">
    <!-- Use TopNav from App.vue but pass project name as prop -->
    <TopNav :project-name="project?.name" />

    <!-- Main Content -->
    <div class="p-6 flex-1">
      <HLSpace vertical :size="24" class="min-h-screen">

    <template v-if="projectLoaded">
      <HLTabs :value="activeTab" @update:value="handleTabChange" :animated="false" size="lg" :maxVisibleTabs="tabsForComponent.length">
        <HLTab v-for="tItem in tabsForComponent" :key="tItem.id" :name="tItem.id">
          <HLIcon size="18" class="mr-1.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tItem.iconColor }" aria-hidden="true">
              <path :d="tItem.svgPath" />
            </svg>
          </HLIcon>
          {{ tItem.label }}
        </HLTab>
      </HLTabs>
    </template>
    <div v-else class="w-full space-y-2">
      <div class="flex items-center gap-3 py-4 header-compact"
      >
        <HLSkeleton circle :width="20" :height="20" aria-hidden="true" />
        <HLSkeleton :width="260" :height="24" aria-hidden="true" />
      </div>
      <HLTabs :value="activeTab" @update:value="handleTabChange" :animated="false" size="lg" :maxVisibleTabs="tabsForComponent.length">
        <HLTab v-for="tItem in tabsForComponent" :key="tItem.id" :name="tItem.id">
          <HLIcon size="18" class="mr-1.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tItem.iconColor }" aria-hidden="true">
              <path :d="tItem.svgPath" />
            </svg>
          </HLIcon>
          {{ tItem.label }}
        </HLTab>
      </HLTabs>
    </div>

      <!-- Tab Content -->
      <!-- OPTIMIZATION: Use keep-alive to cache tab components for instant tab switching -->
      <!-- Note: router-view slot syntax is required for keep-alive with Vue Router -->
      <router-view v-slot="{ Component }">
        <keep-alive :include="['ProjectTestCases', 'ProjectFeatures', 'ProjectBugs', 'ProjectTestRuns', 'ProjectOverview', 'ProjectSettings']" :max="10">
          <component :is="Component" />
        </keep-alive>
      </router-view>
      </HLSpace>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { HLSpace, HLSkeleton, HLTabs, HLTab, HLIcon } from '@platform-ui/highrise'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { usePageTitle } from '@/composables/usePageTitle'
import { projectService } from '@/database/services'
import { t } from '@/i18n/en-us'
import { USER_ROLES } from '@/constants/common'
import { PROJECT_VISIBILITY, getAllProjectTabs, PROJECT_PERMISSIONS, PROJECT_TABS, TAB_ICON_COLORS, getTabIconColor } from '@/constants/projects'
import { STAT_CARD_COLORS } from '@/constants/dashboard'
import { getIconPath } from '@/constants/icons'
import ProjectHeader from '@/components/ui/ProjectHeader.vue'
import TopNav from '@/components/ui/TopNav.vue'

// Stores and composables
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { can } = usePermissions()
const { setTitle } = usePageTitle()

// Reactive data
const project = ref(null)
const refreshKey = ref(0)
const projectLoaded = ref(false)

// Computed
const projectId = computed(() => route.params.projectId)

// Define all project tabs with their metadata (from constants)
const allProjectTabs = computed(() => {
  // Map hyphenated names to camelCase for i18n keys
  const nameToI18nKey = {
    'overview': 'overview',
    'features': 'features',
    'test-cases': 'testCases',
    'test-runs': 'testRuns',
    'bugs': 'bugs',
    'settings': 'settings'
  }
  
  return getAllProjectTabs().map(tab => ({
    ...tab,
    label: t(`project.tabs.${nameToI18nKey[tab.name]}`)
  }))
})

// Filter tabs based on permissions
const visibleProjectTabs = computed(() => {
  return allProjectTabs.value.filter(tab => {
    // Check if tab requires specific permission
    if (tab.requiresPermission) {
      // Map permission name to permission check function
      const permissionMap = {
        [PROJECT_PERMISSIONS.SETTINGS_READ]: () => can.viewSettings(),
        [PROJECT_PERMISSIONS.SETTINGS_WRITE]: () => can.editSettings(),
        [PROJECT_PERMISSIONS.PROJECT_ADMIN]: () => can.manageProjects()
      }
      
      const permissionCheck = permissionMap[tab.requiresPermission]
      if (permissionCheck && !permissionCheck()) {
        return false
      }
    }
    
    return true
  })
})

// Get icon color based on tab ID (using constants)
const getIconColor = (tabId) => {
  return getTabIconColor(tabId)
}

// Prepare tabs for Tabs component (with SVG paths and colors)
const tabsForComponent = computed(() => {
  return visibleProjectTabs.value.map(tab => ({
    id: tab.id,
    label: tab.label,
    svgPath: getIconPath(tab.icon),
    iconColor: getIconColor(tab.id)
  }))
})

// Determine active tab based on current route
const activeTab = computed(() => {
  // Map route names to tab IDs
  const routeToTabMap = {
    'ProjectOverview': PROJECT_TABS.OVERVIEW.id,
    'ProjectFeatures': PROJECT_TABS.FEATURES.id,
    'ProjectTestCases': PROJECT_TABS.TEST_CASES.id,
    'ProjectTestRuns': PROJECT_TABS.TEST_RUNS.id,
    'ProjectBugs': PROJECT_TABS.BUGS.id,
    'ProjectSettings': PROJECT_TABS.SETTINGS.id
  }
  
  return routeToTabMap[route.name] || PROJECT_TABS.OVERVIEW.id
})

// Methods
const handleTabChange = (tabId) => {
  // Find the tab and navigate to its route
  const tab = visibleProjectTabs.value.find(t => t.id === tabId)
  if (tab) {
    router.push({ name: tab.route, params: { projectId: projectId.value } })
  }
}

const loadProject = async () => {
  if (!projectId.value) return
  
  // Guard: Don't load project if user is not authenticated
  if (!authStore.isAuthenticated || !authStore.user) {
    return
  }

  try {
    // On overview tab, get just the project basic info to avoid duplicate overview call
    const isOverviewTab = route.name === 'ProjectOverview'
    if (isOverviewTab) {
      // Get minimal project data for header
      const projectData = await projectService.getById(projectId.value)
      
      const projectName = projectData?.name || projectData?.title || t('project.defaults.untitled')
      project.value = {
        id: projectData?.id || projectId.value,
        name: projectName
      }
      projectLoaded.value = true
      
      // Update page title with project name and current tab
      const tabLabel = tabsForComponent.value.find(t => t.id === activeTab.value)?.label || ''
      setTitle(tabLabel ? `${projectName} - ${tabLabel}` : projectName)
      return
    }

    // Load project using projectService for other tabs
    const projectData = await projectService.getById(projectId.value)

    if (projectData) {
      // Check access rights for private projects
      const isSuperAdmin = authStore.userProfile?.role?.toLowerCase() === USER_ROLES.SUPER_ADMIN.toLowerCase()
      const currentUserId = authStore.user?.uid
      
      if (projectData.visibility === PROJECT_VISIBILITY.PRIVATE) {
        // Private projects are only accessible to Super Admin and project members
        const hasAccess = isSuperAdmin || (projectData.members && projectData.members.includes(currentUserId))
        
        if (!hasAccess) {
          toast.error(t('project.messages.accessDenied'))
          router.push('/dashboard')
          return
        }
      }

      // Store minimal project data - only what's needed
      const projectName = projectData.name || projectData.title || t('project.defaults.untitled')
      project.value = {
        id: projectData.id,
        name: projectName
      }
      projectLoaded.value = true
      
      // Update page title with project name and current tab
      const tabLabel = tabsForComponent.value.find(t => t.id === activeTab.value)?.label || ''
      setTitle(tabLabel ? `${projectName} - ${tabLabel}` : projectName)
    } else {
      toast.error(t('project.messages.notFound'))
      project.value = null
    }
  } catch (error) {
    toast.error(t('project.messages.loadingError'))
    project.value = null
  }
}

// Lifecycle
onMounted(() => {
  loadProject()
})

// Watch for route changes
watch(() => route.params.projectId, () => {
  projectLoaded.value = false
  loadProject()
})

// Watch for tab changes to update title
watch([() => activeTab.value, () => project.value?.name], () => {
  if (project.value?.name) {
    const tabLabel = tabsForComponent.value.find(t => t.id === activeTab.value)?.label || ''
    setTitle(tabLabel ? `${project.value.name} - ${tabLabel}` : project.value.name)
  }
})
</script>

<style scoped>
.header-compact :deep(h4) {
  margin: 0;
  font-size: 1.25rem; /* 20px */
  line-height: 1.75rem; /* 28px */
}

/* Remove top margin from tabs */
:deep(.hl-tabs) {
  margin-top: 0 !important;
}
</style>
